import React, { useState } from "react";
import api from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Clock } from "lucide-react";
import { toast } from "sonner";

/**
 * Student grace-period request dialog.
 *
 * Pops on the profile / learn page when the student wants more time on a
 * program whose access window is closing or has lapsed. Backend enforces a
 * single pending request per enrollment, so the dialog is intentionally
 * minimal — just days + reason + submit.
 */
export default function ExtensionRequestDialog({
    open,
    onOpenChange,
    enrollment,
    onSubmitted,
}) {
    const [days, setDays] = useState(14);
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);

    if (!enrollment) return null;

    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        const n = Number(days);
        if (!n || n < 1 || n > 365) {
            toast.error("Please choose between 1 and 365 days.");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post(
                `/me/enrollments/${enrollment.id}/extension-request`,
                {
                    requested_days: n,
                    reason: reason.trim() || undefined,
                },
            );
            toast.success(
                "Request submitted. The admin will get back to you shortly.",
            );
            onSubmitted?.(data);
            onOpenChange(false);
            setReason("");
            setDays(14);
        } catch (err) {
            toast.error(
                err?.response?.data?.detail ||
                    "Could not submit your request. Try again.",
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-md"
                data-testid="extension-request-dialog"
            >
                <DialogHeader>
                    <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold flex items-center gap-2">
                        <Clock className="h-3.5 w-3.5" />
                        Grace period
                    </div>
                    <DialogTitle className="font-display text-2xl text-primary">
                        Request more time
                    </DialogTitle>
                    <DialogDescription>
                        Asking for grace on{" "}
                        <strong>
                            {enrollment.program_title || "this program"}
                        </strong>
                        . Your current access window ends{" "}
                        <strong>
                            {enrollment.end_date || enrollment.access_window?.end_date || "—"}
                        </strong>
                        .
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                    <div>
                        <Label htmlFor="ext-days" className="text-sm font-semibold">
                            How many extra days?
                        </Label>
                        <div className="flex gap-2 mt-1.5">
                            {[7, 14, 30, 60].map((d) => (
                                <button
                                    type="button"
                                    key={d}
                                    onClick={() => setDays(d)}
                                    className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${
                                        Number(days) === d
                                            ? "bg-primary text-primary-foreground border-primary"
                                            : "bg-background border-border hover:bg-muted"
                                    }`}
                                    data-testid={`extension-quick-${d}`}
                                >
                                    {d}d
                                </button>
                            ))}
                            <Input
                                id="ext-days"
                                type="number"
                                min={1}
                                max={365}
                                value={days}
                                onChange={(e) => setDays(e.target.value)}
                                className="w-24"
                                data-testid="extension-days-input"
                            />
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="ext-reason" className="text-sm font-semibold">
                            Why? <span className="font-normal text-muted-foreground">(optional)</span>
                        </Label>
                        <Textarea
                            id="ext-reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="A short note helps the admin make a quick decision."
                            rows={3}
                            maxLength={2000}
                            data-testid="extension-reason-input"
                        />
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            data-testid="extension-cancel-btn"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={saving}
                            className="bg-primary hover:bg-primary/90"
                            data-testid="extension-submit-btn"
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                    Sending…
                                </>
                            ) : (
                                "Submit request"
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
